import React, { useEffect, useState, type JSX } from 'react';
import constants from '../../../common/constants';
import { ContentWorkflow, WorkflowEventTypes } from '@amzn/awscat-pattern-library-service-model';
import {
  Box,
  FormField,
  Grid,
  InputProps,
  NonCancelableCustomEvent,
  SpaceBetween,
  Textarea,
} from '@amzn/awsui-components-core';
import { createWorkflowEventWithStatePolling } from '../../../api/CreateWorkflowEventWithStatePolling';
import ModalDialogButtonV3 from '../../common/ModalDialogButtonV3';
import { MplServiceClientFactory } from '../../../api/MplServiceClientFactory';

interface Props {
  contentId: string;
  disabled: boolean;
  onReviewComplete?: () => void;
  reviewComment?: string;
  workflowId: number;
  workflow: ContentWorkflow;
  approvalDisabled?: boolean;
  disableApproveMessage?: string;
}

const ReviewChecklistControls: React.FunctionComponent<Props> = (props: Props) => {
  const {
    contentId,
    disabled,
    onReviewComplete,
    reviewComment = '',
    workflowId,
    workflow,
    approvalDisabled,
    disableApproveMessage,
  } = props;
  const [comment, setComment] = useState<string>();
  const [commentValid, setCommentValid] = useState<boolean>(false);
  const [cannotApproveMessage, setCannotApproveMessage] = useState<string>('');
  const [canApprove, setCanApprove] = useState<boolean>(true);

  const mplServiceClient = MplServiceClientFactory.create();

  useEffect(() => {
    if (!disabled && !approvalDisabled) {
      setCannotApproveMessage('');
      setCanApprove(true);
      setComment('');
    }
  }, [disabled, approvalDisabled]);

  const handleCommentChange = (e: NonCancelableCustomEvent<InputProps.ChangeDetail>) => {
    const updatedComment = e.detail.value;
    setComment(updatedComment);
    setCommentValid(updatedComment && updatedComment.length > 0 ? true : false);
  };

  const completeReview = async (eventType: WorkflowEventTypes) => {
    const eventComment = `${reviewComment} ${comment}`.trim();
    await createWorkflowEventWithStatePolling(contentId, workflowId, eventType, eventComment);
    // @ts-expect-error TS2722 - strict migration
    await onReviewComplete();
  };

  const disapprove = async () => {
    await completeReview(WorkflowEventTypes.DISAPPROVE);
  };

  const ableToApprove = async () => {
    const currContentWorkflow = (await mplServiceClient.getContentWorkflow(contentId)).workflow;
    if (currContentWorkflow.lock) {
      setCannotApproveMessage(constants.DISABLE_APPROVE_WHEN_EDITING_MESSAGE);
      setCanApprove(false);
      setComment('');
      return false;
    } else if (currContentWorkflow.latestVersionNumber > workflow.latestVersionNumber) {
      setCannotApproveMessage(constants.DISABLE_APPROVE_WHEN_OUTDATED_MESSAGE);
      setCanApprove(false);
      setComment('');
      return false;
    }
    setCannotApproveMessage('');
    setCanApprove(true);
    return true;
  };

  const approve = async () => {
    if (await ableToApprove()) {
      await completeReview(WorkflowEventTypes.APPROVE);
    }
  };

  const approvalMessage = (
    <Box>{cannotApproveMessage !== '' ? cannotApproveMessage : disableApproveMessage}</Box>
  );

  const commentField = (): JSX.Element => {
    return (
      <Grid gridDefinition={[{ colspan: 12 }]}>
        <FormField stretch>
          {/* @ts-expect-error TS2322 - strict migration */}
          <Textarea onChange={handleCommentChange} value={comment} rows={3} ariaRequired />
        </FormField>
      </Grid>
    );
  };

  return (
    <Box margin={{ top: 'xl' }}>
      <SpaceBetween data-testid="reviewer-checklist-buttons" direction={'vertical'} size={'xxs'}>
        {approvalMessage}
        <ModalDialogButtonV3
          variant="primary"
          header={constants.REVIEW_APPROVE_MODAL_HEADER}
          text={constants.REVIEW_APPROVE_MODAL_COMMENT_HEADER}
          onSubmit={approve}
          submitButtonText={constants.APPROVE_BUTTON_TEXT}
          inputForm={commentField}
          disabled={disabled || approvalDisabled || !canApprove}
          inputValid={commentValid}
          fullwidth
        />
      </SpaceBetween>
    </Box>
  );
};

export default ReviewChecklistControls;
